import { Directive, Input, ElementRef, Renderer2, OnInit, OnDestroy } from '@angular/core';
import { Overlay, OverlayRef, OverlayPositionBuilder } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { TooltipComponent } from '../components/tooltip/tooltip.component';

/**
 * Directive that automatically truncates text with ellipsis and shows a tooltip with the full text on hover.
 * Combines CSS text truncation with tooltip functionality in a single, reusable directive.
 * Supports both single-line and multi-line truncation.
 *
 * @example
 * // Basic usage - single-line truncation
 * <a appTruncateWithTooltip [maxWidth]="300">https://very-long-url.com</a>
 * <span appTruncateWithTooltip>Long text content here...</span>
 *
 * @example
 * // Multi-line truncation (4 lines)
 * <p appTruncateWithTooltip [lineClamp]="4" [maxWidth]="600">Very long description...</p>
 *
 * @example
 * // With custom max-width
 * <div appTruncateWithTooltip [maxWidth]="200">Very long text...</div>
 *
 * @example
 * // No max-width — truncates at the container's width (responsive cards, tables)
 * <h3 appTruncateWithTooltip [maxWidth]="null" [tooltipText]="title">{{ title }}</h3>
 *
 * @example
 * // With custom tooltip text (different from displayed text)
 * <a appTruncateWithTooltip [tooltipText]="fullUrl">{{ shortUrl }}</a>
 *
 * @example
 * // With custom tooltip delay
 * <span appTruncateWithTooltip [tooltipShowDelay]="500">Long content</span>
 */
@Directive({
  selector: '[appTruncateWithTooltip]',
  standalone: false,
  host: {
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()'
  }
})
export class TruncateWithTooltipDirective implements OnInit, OnDestroy {
  /**
   * Maximum width for the truncated element in pixels.
   * Pass `null` to set no max-width, so the element truncates at whatever width
   * its container gives it (use this inside responsive layouts such as cards,
   * where a fixed cap would truncate the text before the space runs out).
   * @default 400
   */
  @Input() maxWidth: number | null = 400;

  /**
   * Number of lines to show before truncating (multi-line truncation)
   * If not provided, uses single-line truncation
   */
  @Input() lineClamp?: number;

  /**
   * Custom tooltip text (if different from element text content)
   * If not provided, uses the element's text content
   */
  @Input() tooltipText?: string;

  /**
   * Delay before showing tooltip in milliseconds
   * @default 300
   */
  @Input() tooltipShowDelay: number = 300;

  /**
   * Delay before hiding tooltip in milliseconds
   * @default 0
   */
  @Input() tooltipHideDelay: number = 0;

  /**
   * Whether to disable the tooltip
   * @default false
   */
  @Input() tooltipDisabled: boolean = false;

  private overlayRef: OverlayRef | null = null;
  private showTimeout: any;
  private hideTimeout: any;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder
  ) {}

  ngOnInit(): void {
    this.applyTruncationStyles();
  }

  /**
   * Applies CSS styles for text truncation
   * @private
   */
  private applyTruncationStyles(): void {
    const element = this.elementRef.nativeElement;

    if (this.lineClamp && this.lineClamp > 1) {
      // Multi-line truncation
      this.renderer.setStyle(element, 'display', '-webkit-box');
      this.renderer.setStyle(element, '-webkit-box-orient', 'vertical');
      this.renderer.setStyle(element, '-webkit-line-clamp', this.lineClamp.toString());
      this.renderer.setStyle(element, 'overflow', 'hidden');
      this.renderer.setStyle(element, 'text-overflow', 'ellipsis');
      this.renderer.setStyle(element, 'word-wrap', 'break-word');
    } else {
      // Single-line truncation
      this.renderer.setStyle(element, 'display', 'block');
      this.renderer.setStyle(element, 'overflow', 'hidden');
      this.renderer.setStyle(element, 'text-overflow', 'ellipsis');
      this.renderer.setStyle(element, 'white-space', 'nowrap');
    }

    // `maxWidth: null` leaves the width to the layout; the element still truncates
    // (and the tooltip still triggers) at its container's width.
    if (this.maxWidth !== null) {
      this.renderer.setStyle(element, 'max-width', `${this.maxWidth}px`);
    }
  }

  /**
   * Handles mouse enter event to show tooltip
   */
  onMouseEnter(): void {
    if (this.tooltipDisabled) {
      return;
    }

    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    this.showTimeout = setTimeout(() => {
      this.show();
    }, this.tooltipShowDelay);
  }

  /**
   * Handles mouse leave event to hide tooltip
   */
  onMouseLeave(): void {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }

    if (this.overlayRef) {
      this.hideTimeout = setTimeout(() => {
        this.hide();
      }, this.tooltipHideDelay);
    }
  }

  /**
   * Shows the tooltip
   * @private
   */
  private show(): void {
    if (this.overlayRef) {
      return;
    }

    // Check if text is actually truncated before showing tooltip
    const element = this.elementRef.nativeElement;
    const isTruncated = this.lineClamp && this.lineClamp > 1
      ? element.scrollHeight > element.clientHeight
      : element.scrollWidth > element.clientWidth;

    // Don't show tooltip if text is not truncated
    if (!isTruncated) {
      return;
    }

    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions([
        {
          originX: 'end',
          originY: 'center',
          overlayX: 'start',
          overlayY: 'center',
          offsetX: 8
        },
        {
          originX: 'start',
          originY: 'center',
          overlayX: 'end',
          overlayY: 'center',
          offsetX: -8
        },
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetY: -8
        },
        {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          offsetY: 8
        }
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
      panelClass: 'truncate-tooltip-overlay'
    });

    const tooltipPortal = new ComponentPortal(TooltipComponent);
    const tooltipRef = this.overlayRef.attach(tooltipPortal);

    // Get tooltip text (use provided text or element's text content)
    const tooltipContent = this.tooltipText || this.elementRef.nativeElement.textContent?.trim() || '';
    tooltipRef.instance.text = tooltipContent;
  }

  /**
   * Hides the tooltip
   * @private
   */
  private hide(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  /**
   * Cleanup on directive destroy
   */
  ngOnDestroy(): void {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    this.hide();
  }
}
